import React, { useState, useRef, useEffect } from 'react';
import { Terminal, Sparkles } from 'lucide-react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

interface CommandOutput {
  command: string;
  output: string;
  isError?: boolean;
}

interface CommandLineProps {
  initialCommand?: string;
}

const CommandLine: React.FC<CommandLineProps> = ({ initialCommand = '' }) => {
  const [command, setCommand] = useState(initialCommand);
  const [history, setHistory] = useState<CommandOutput[]>([
    { command: 'system', output: 'AI Network Automation CLI v1.0.0 powered by S2AI with GROK enhancement' },
    { command: 'system', output: 'Type "help" for available commands' },
  ]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const terminalRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [history]);

  // Handle initial command if provided
  useEffect(() => {
    if (initialCommand) {
      handleCommand(new Event('submit') as unknown as React.FormEvent);
    }
  }, []);

  const addToHistory = (cmd: string, out: string, error: boolean = false) => {
    setHistory(prev => [...prev, { command: cmd, output: out, isError: error }]);
  };

  const handleCommand = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!command.trim()) return;
    
    const commandLower = command.toLowerCase().trim();
    let output = '';
    let isError = false;
    
    // Process commands
    if (commandLower === 'help') {
      output = `
Available commands:
  help                 - Show this help message
  status               - Show network status
  list devices         - List all devices
  list networks        - List all networks
  deploy mesh <name>   - Deploy a new mesh network
  configure vm <id>    - Configure a VM
  scan network         - Scan for network issues
  s2ai analyze <topic> - Use S2AI with GROK to analyze a topic
  grok analyze <topic> - Alternative syntax for S2AI with GROK analysis
  clear                - Clear terminal
      `;
    } else if (commandLower === 'clear') {
      setHistory([]);
      setCommand('');
      return;
    } else if (commandLower === 'status') {
      output = 'All networks operational. 42 devices online. 3 pending updates. S2AI with GROK monitoring enabled.';
    } else if (commandLower === 'list devices') {
      output = `
ID    | Name           | Type      | Status   
------|----------------|-----------|----------
dev01 | Edge Router A  | Router    | Online   
dev02 | Cloud Node B   | VM        | Online   
dev03 | IoT Gateway    | Gateway   | Online   
dev04 | Mesh Node 1    | Mesh      | Updating 
dev05 | Mesh Node 2    | Mesh      | Online   
      `;
    } else if (commandLower === 'list networks') {
      output = `
ID      | Name                   | Type    | Nodes 
--------|------------------------|---------|-------
net-101 | Google Cloud Mesh East | Cloud   | 12    
net-102 | AWS Container Network  | Cloud   | 24    
net-103 | Azure Enterprise Mesh  | Hybrid  | 18    
net-104 | ATT Fiber Backbone     | Fiber   | 8     
net-105 | Verizon 5G Edge Network| 5G      | 15    
      `;
    } else if (commandLower.startsWith('deploy mesh')) {
      const name = command.split('deploy mesh ')[1];
      if (name) {
        output = `Initiating deployment of mesh network "${name}"...\nS2AI with GROK optimizing deployment strategy...\nDeployment queued. Run 'status' to check progress.`;
        toast.success(`Deployment of "${name}" initiated`, {
          description: "S2AI with GROK is optimizing the deployment"
        });
      } else {
        output = 'Error: Please specify a name for the mesh network.';
        isError = true;
      }
    } else if (commandLower.startsWith('configure vm')) {
      const id = command.split('configure vm ')[1];
      if (id) {
        output = `Starting configuration of VM with ID "${id}"...\nS2AI with GROK determining optimal configuration settings...\nConfiguration in progress. This may take a few minutes.`;
      } else {
        output = 'Error: Please specify a VM ID.';
        isError = true;
      }
    } else if (commandLower === 'scan network') {
      setIsProcessing(true);
      addToHistory(command, 'Network scan initiated. S2AI with GROK scanning for issues and vulnerabilities...');
      
      // Simulate S2AI processing with GROK
      setTimeout(() => {
        const scanResults = `
Scan completed with GROK-enhanced intelligence. Results:
- 42 devices scanned
- 3 devices have outdated firmware
- 1 potential security vulnerability detected (CVE-2023-3589)
- Network performance: 93% of optimal
- Latency within acceptable ranges

Recommendations:
1. Update firmware on devices dev04, dev09, dev17
2. Apply security patch to device dev02
3. Rebalance traffic on Google Cloud Mesh East for better performance
`;
        addToHistory('system', scanResults);
        toast.info('S2AI with GROK network scan completed');
        setIsProcessing(false);
      }, 3000);
      
      setCommand('');
      return;
    } else if (commandLower.startsWith('analyze') || commandLower.startsWith('s2ai analyze') || commandLower.startsWith('grok analyze')) {
      const topic = commandLower.startsWith('s2ai analyze') 
        ? command.split('s2ai analyze ')[1] 
        : commandLower.startsWith('grok analyze')
          ? command.split('grok analyze ')[1]
          : command.split('analyze ')[1];
      
      if (topic) {
        setIsProcessing(true);
        addToHistory(command, `S2AI with GROK analysis of "${topic}" in progress...\nProcessing network data and generating intelligent insights.`);
        
        // Simulate S2AI processing with GROK
        setTimeout(() => {
          let analysisResults = '';
          
          if (topic.includes('performance') || topic.includes('speed')) {
            analysisResults = `
S2AI Performance Analysis Complete with GROK enhancement:
- Network throughput at 87% capacity
- Identified 2 bottlenecks in the ATT Fiber Backbone
- Google Cloud Mesh East showing optimal performance
- AWS Container Network experiencing intermittent packet loss (0.5%)

Recommended Actions:
1. Increase bandwidth allocation to ATT Fiber Backbone nodes 3 and 7
2. Implement caching at edge nodes to reduce latency
3. Schedule non-peak hours update for AWS Container Network`;
          } else if (topic.includes('security') || topic.includes('vulnerabilities')) {
            analysisResults = `
S2AI Security Analysis Complete with GROK enhancement:
- No critical vulnerabilities detected
- 3 medium-risk issues found in edge devices
- Encryption protocols up-to-date on 98% of devices
- Unusual traffic pattern detected from device dev17

Recommended Actions:
1. Apply security patches to devices dev08, dev17, dev23
2. Update firewall rules for edge devices
3. Implement additional authentication for admin access
4. Investigate traffic pattern from device dev17`;
          } else {
            analysisResults = `
S2AI Analysis of "${topic}" Complete with GROK enhancement:
- Generated comprehensive assessment using GROK's advanced algorithms
- Identified 4 potential optimization opportunities
- Analyzed historical performance data
- Created predictive model for future requirements

Recommended Actions:
1. Review detailed report in the Analytics dashboard
2. Schedule follow-up analysis in 30 days
3. Consider implementing suggested optimizations`;
          }
          
          addToHistory('system', analysisResults);
          toast.success(`S2AI with GROK analysis of "${topic}" completed`);
          setIsProcessing(false);
        }, 4000);
        
        setCommand('');
        return;
      } else {
        output = 'Error: Please specify what to analyze.';
        isError = true;
      }
    } else {
      output = `Command not recognized: ${command}\nType "help" for available commands.`;
      isError = true;
    }
    
    addToHistory(command, output, isError);
    setCommand('');
    setHistoryIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (historyIndex < history.length - 1) {
        const newIndex = historyIndex + 1;
        setHistoryIndex(newIndex);
        const cmd = history[history.length - 1 - newIndex];
        if (cmd.command !== 'system') {
          setCommand(cmd.command);
        }
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex > 0) {
        const newIndex = historyIndex - 1;
        setHistoryIndex(newIndex);
        const cmd = history[history.length - 1 - newIndex];
        if (cmd.command !== 'system') {
          setCommand(cmd.command);
        }
      } else {
        setHistoryIndex(-1);
        setCommand('');
      }
    } else if (e.key === 'Tab') {
      e.preventDefault();
      // Basic autocomplete
      if (command.startsWith('l')) {
        setCommand('list ');
      } else if (command.startsWith('list ')) {
        if (command === 'list ') {
          setCommand('list devices');
        }
      } else if (command.startsWith('d')) {
        setCommand('deploy mesh ');
      } else if (command.startsWith('c')) {
        setCommand('configure vm ');
      } else if (command.startsWith('s')) {
        if (command === 's') {
          setCommand('status');
        } else if (command.startsWith('s2')) {
          setCommand('s2ai analyze ');
        } else if (command.startsWith('sc')) {
          setCommand('scan network');
        }
      } else if (command.startsWith('h')) {
        setCommand('help');
      } else if (command.startsWith('g')) {
        setCommand('s2ai analyze '); // Redirect to s2ai for backward compatibility
      } else if (command.startsWith('a')) {
        setCommand('s2ai analyze '); // Redirect to s2ai for convenience
      }
    }
  };

  return (
    <Card className="w-full h-full flex flex-col border border-border bg-card rounded-lg shadow-md">
      <CardHeader className="px-4 py-2 bg-muted/50 rounded-t-lg border-b border-border flex-shrink-0">
        <CardTitle className="text-sm flex items-center">
          <Terminal className="h-4 w-4 mr-2" />
          Network Command Line
          <Sparkles className="h-4 w-4 ml-2 text-primary" />
          {isProcessing && <span className="ml-auto text-xs text-muted-foreground animate-pulse">S2AI with GROK processing...</span>}
        </CardTitle>
      </CardHeader>
      <CardContent 
        className="p-0 overflow-auto flex-grow flex-shrink-1 font-mono text-xs"
        ref={terminalRef}
      >
        <div className="p-4 space-y-2">
          {history.map((item, index) => (
            <div key={index}>
              {item.command !== 'system' && (
                <div className="flex gap-2">
                  <span className="text-primary">$</span>
                  <span>{item.command}</span>
                </div>
              )}
              <div className={`whitespace-pre-wrap pl-4 ${item.isError ? 'text-destructive' : ''}`}>
                {item.output}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
      <CardFooter className="p-2 border-t border-border">
        <form onSubmit={handleCommand} className="flex w-full">
          <span className="text-primary flex items-center px-2">$</span>
          <Input
            ref={inputRef}
            type="text"
            value={command}
            onChange={(e) => setCommand(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type command (try 'help' to start)"
            className="flex-grow border-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent"
            disabled={isProcessing}
            autoFocus
          />
          <Button 
            type="submit" 
            variant="ghost" 
            size="sm" 
            className="ml-2"
            disabled={isProcessing}
          >
            Run
          </Button>
        </form>
      </CardFooter>
    </Card>
  );
};

export default CommandLine;
